import { useEffect, useRef } from 'react';
import type { Job } from '../redux/features/jobs/jobsSlice';

/**
 * Hook to track when a job card enters the viewport
 * Automatically records the job as "browsed" when it becomes visible
 */
export function useBrowsedJobTracking(
  job: Job | null | undefined,
  recordBrowsedJob: (job: Job) => Promise<void>,
  enabled: boolean = true
) {
  const elementRef = useRef<HTMLElement | null>(null);
  const hasBeenBrowsedRef = useRef(false);

  useEffect(() => {
    if (!enabled || !job || !job.id || hasBeenBrowsedRef.current) {
      return;
    }

    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      return;
    }

    const element = elementRef.current;
    if (!element) {
      return;
    }

    const observerOptions = {
      root: null, // Use viewport as root
      rootMargin: '0px', // No margin - trigger when element enters viewport
      threshold: 0.1, // Trigger when 10% of element is visible
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !hasBeenBrowsedRef.current) {
          hasBeenBrowsedRef.current = true;
          // Record as browsed when it enters viewport
          void recordBrowsedJob(job);
          // Unobserve after recording to avoid duplicate calls
          observer.unobserve(entry.target);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);
    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [job, recordBrowsedJob, enabled]);

  return elementRef;
}

